"use client"

import {
  useEffect, useId, useLayoutEffect, useRef, useState,
} from "react"
import type { KeyboardEvent, PointerEvent } from "react"

import { EcobeeDisplayTransition } from "./EcobeeDisplayTransition"
import { NestLearningDial } from "./NestLearningDial"
import {
  NEST_LEARNING_LIMITS, nestAngleDelta, nestPoint, nestRingSetPoint, nestTemperatureAngle,
} from "./nest-learning-state"
import type { NestDisplayState, NestFarsightFace } from "./nest-learning-state"
import {
  adjustThermostatSetPoint, celsiusToDisplay, formatTemperature, getThermostatSetPointBounds, setThermostatHvacMode,
} from "./thermostat-state"
import type {
  TemperatureUnit, ThermostatClimateSetting, ThermostatSetPoint, ThermostatSetPointLimits,
} from "./thermostat-state"

import styles from "./NestLearningThermostat.module.css"
import nestLogo from "./assets/nest.svg"

export type { NestDisplayState, NestFarsightFace } from "./nest-learning-state"

export type NestLearningThermostatProps = {
  appearance?: "realistic" | "clay"
  temperatureCelsius: number
  climateSetting: ThermostatClimateSetting
  temperatureUnit?: TemperatureUnit
  isHeating?: boolean
  isCooling?: boolean
  isFanRunning?: boolean
  /** Show the home-screen fan icon independently of the reported fan state. */
  showFanIndicator?: boolean
  /** Animate reported heating/cooling progress along the dial; hosts can disable the repeating pulse. */
  animateActivity?: boolean
  /** Keep the dial, activity pulse, and digit fades in sync with a host demo's pause control. */
  displayPaused?: boolean
  displayState?: NestDisplayState
  onDisplayStateChange?: (state: NestDisplayState) => void
  farsightFace?: NestFarsightFace
  time?: string
  eco?: boolean
  onEcoChange?: (eco: boolean) => void
  showLeaf?: boolean
  /** Supplied example/readout, never calculated as an equipment prediction. */
  timeToTemperature?: string
  selectedTarget?: ThermostatSetPoint
  onSelectedTargetChange?: (target: ThermostatSetPoint) => void
  onClimateSettingChange?: (setting: ThermostatClimateSetting) => void
  setPointLimits?: ThermostatSetPointLimits
  disabled?: boolean
  /** Reset local menus without remounting the drawing or interrupting screen transitions. */
  resetKey?: string | number
  onInteraction?: () => void
  className?: string
}

type Panel = "home" | "quick" | "mode" | "fan" | "eco" | "settings"
type Icon = "done" | "mode" | "fan" | "eco" | "settings"
const QUICK = [
  { icon: "done", label: "Done", panel: "home" },
  { icon: "mode", label: "Mode", panel: "mode" },
  { icon: "fan", label: "Fan", panel: "fan" },
  { icon: "eco", label: "Eco", panel: "eco" },
  { icon: "settings", label: "Settings", panel: "settings" },
] as const satisfies readonly { icon: Icon, label: string, panel: Panel }[]
const MODES = [
  { value: "heat", label: "Heat" },
  { value: "cool", label: "Cool" },
  { value: "heat_cool", label: "Heat • Cool" },
  { value: "off", label: "Off" },
] as const
const RING_PATH = "M200 27a173 173 0 1 1 0 346a173 173 0 1 1 0-346Z M200 88a112 112 0 1 0 0 224a112 112 0 1 0 0-224Z"

function NestIcon({
  icon, x, y, color = "currentColor",
}: {
  icon: Icon
  x: number
  y: number
  color?: string
}) {
  return (
    <g
      transform={`translate(${x} ${y})`}
      fill="none"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {icon === "done" && (
        <path
          d="m-6 0 4 4 8-8"
          strokeWidth="2.5"
        />
      )}
      {icon === "mode" && [-4, 0, 4].map((dy) => (
        <path
          key={dy}
          d={`M-8 ${dy}c6-7 10 7 16 0`}
        />
      ))}
      {icon === "fan" && (
        <g
          fill={color}
          stroke="none"
        >
          {[0, 90, 180, 270].map((angle) => (
            <path
              key={angle}
              transform={`rotate(${angle})`}
              d="M1-2C-7-4-5-12 1-10C5-9 5-4 1-2Z"
            />
          ))}
          <circle r="2" />
        </g>
      )}
      {icon === "eco" && (
        <path
          d="M-8 6C-13-6 2-5 10-10C11 5 4 12-5 9C-3 3 3 0 6-4C0 0-4 1-8 6Z"
          fill={color}
          stroke="none"
        />
      )}
      {icon === "settings" && (
        <g>
          <circle r="6" />
          <circle r="2" />
          {Array.from({ length: 8 }, (_, i) => (
            <path
              key={i}
              d="M0-6V-9"
              transform={`rotate(${i * 45})`}
              strokeWidth="2.5"
            />
          ))}
        </g>
      )}
    </g>
  )
}

/** Third-generation ring interaction, with controlled climate and reported equipment state. */
export function NestLearningThermostat({
  appearance = "realistic", temperatureCelsius, climateSetting, temperatureUnit = "fahrenheit",
  isHeating = false, isCooling = false, isFanRunning = false, showFanIndicator = true,
  animateActivity = true, displayPaused = false,
  displayState = "awake", onDisplayStateChange, farsightFace = "target", time = "10:09",
  eco = false, onEcoChange, showLeaf = false, timeToTemperature,
  selectedTarget = "heating", onSelectedTargetChange, onClimateSettingChange,
  setPointLimits = NEST_LEARNING_LIMITS, disabled = false, resetKey, onInteraction, className,
}: NestLearningThermostatProps) {
  const id = useId().replace(/:/g, "")
  const svg = useRef<SVGSVGElement>(null)
  const [panel, setPanel] = useState<Panel>("home")
  const [selection, setSelection] = useState(0)
  const [dragging, setDragging] = useState(false)
  const [reducedMotion, setReducedMotion] = useState(false)
  const drag = useRef<{
    pointer: number
    angle: number
    rotation: number
    value: number
    selection: number
    moved: boolean
  } | null>(null)
  const clay = appearance === "clay"
  const mode = climateSetting.hvac_mode_setting
  const target = mode === "heat" ? "heating" : mode === "cool" ? "cooling" : selectedTarget
  const field = target === "heating" ? "heating_set_point_celsius" : "cooling_set_point_celsius"
  const value = climateSetting[field]
  const bounds = getThermostatSetPointBounds(climateSetting, target, setPointLimits)
  const activity = isHeating ? "heating" : isCooling ? "cooling" : "idle"
  const awake = displayState === "awake"
  const home = panel === "home"
  const canAdjust = mode !== "off" && !eco && !!onClimateSettingChange && !disabled
  const number = formatTemperature(value, temperatureUnit)
  const ambient = formatTemperature(temperatureCelsius, temperatureUnit)
  const targetAngle = nestTemperatureAngle(value, setPointLimits)
  const ambientAngle = nestTemperatureAngle(temperatureCelsius, setPointLimits)
  const heatingTarget = climateSetting.heating_set_point_celsius
  const coolingTarget = climateSetting.cooling_set_point_celsius
  const activityTarget = isHeating && (mode === "heat" || mode === "heat_cool") && temperatureCelsius < heatingTarget
    ? heatingTarget
    : !isHeating && isCooling && (mode === "cool" || mode === "heat_cool") && temperatureCelsius > coolingTarget
      ? coolingTarget
      : undefined
  const oppositeValue = climateSetting[target === "heating" ? "cooling_set_point_celsius" : "heating_set_point_celsius"]
  const status = isHeating ? "HEATING" : isCooling ? "COOLING" : mode === "heat" ? "HEAT SET TO" : mode === "cool" ? "COOL SET TO" : "HEAT • COOL"
  const displayStatus = activity !== "idle" && timeToTemperature ? timeToTemperature : status
  const items = panel === "mode"
    ? MODES.map(({ label }) => label)
    : panel === "fan"
      ? ["Auto", "On", "Back"]
      : panel === "eco"
        ? [eco ? "Stop Eco" : "Start Eco", "Back"]
        : panel === "settings" ? ["Farsight", "Sleep", "Back"] : []
  const quickSelection = QUICK[selection] ?? QUICK[0]
  const modeSelection = MODES[selection] ?? MODES[0]
  const count = panel === "quick" ? QUICK.length : items.length
  const ink = clay ? "#66707e" : "#fff"
  const secondary = clay ? "#929ba8" : "#76797b"
  const leafColor = clay ? "#88a087" : "#6adb39"

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)")
    const update = () => setReducedMotion(media.matches)
    update()
    media.addEventListener("change", update)
    return () => media.removeEventListener("change", update)
  }, [])

  useEffect(() => {
    setPanel("home")
    setSelection(0)
    drag.current = null
    setDragging(false)
  }, [displayState, disabled, mode, eco, resetKey])

  useLayoutEffect(() => {
    if (!displayPaused) {
      return undefined
    }
    // Freeze in-flight color fades and the room marker as well as the dial's own animation.
    const transitions = svg.current?.getAnimations({ subtree: true })
      .filter((animation) => animation instanceof CSSTransition) ?? []
    transitions.forEach((animation) => animation.pause())
    return () => {
      transitions.forEach((animation) => {
        if (animation.playState === "paused") {
          animation.play()
        }
      })
    }
  }, [displayPaused])

  function interact() {
    onInteraction?.()
    if (!awake) {
      onDisplayStateChange?.("awake")
      return false
    }
    return true
  }

  function open(next: Panel) {
    setPanel(next)
    setSelection(next === "mode"
      ? Math.max(0, MODES.findIndex((item) => item.value === mode))
      : next === "fan" && climateSetting.fan_mode_setting === "on" ? 1 : 0)
  }

  function press() {
    if (disabled || !interact()) {
      return
    }
    if (home) {
      open("quick")
    }
    else if (panel === "quick") {
      open(quickSelection.panel)
    }
    else if (panel === "mode") {
      onEcoChange?.(false)
      onClimateSettingChange?.(setThermostatHvacMode(climateSetting, modeSelection.value, setPointLimits))
      open("home")
    }
    else if (panel === "fan") {
      if (selection < 2) {
        onClimateSettingChange?.({ ...climateSetting, fan_mode_setting: selection === 0 ? "auto" : "on" })
      }
      open("home")
    }
    else if (panel === "eco") {
      if (selection === 0) {
        onEcoChange?.(!eco)
      }
      open("home")
    }
    else if (panel === "settings") {
      if (selection < 2) {
        onDisplayStateChange?.(selection === 0 ? "farsight" : "asleep")
        open("home")
      }
      else {
        open("quick")
      }
    }
  }

  function turn(direction: -1 | 1) {
    if (disabled || !interact()) {
      return
    }
    if (!home) {
      setSelection((previous) => (previous + direction + count) % count)
    }
    else if (canAdjust) {
      const next = adjustThermostatSetPoint(climateSetting, target, direction, temperatureUnit, setPointLimits)
      onClimateSettingChange?.(next)
    }
  }

  function keyboard(event: KeyboardEvent) {
    if (disabled) {
      return
    }
    if (["ArrowUp", "ArrowRight", "ArrowDown", "ArrowLeft"].includes(event.key)) {
      event.preventDefault()
      turn(event.key === "ArrowUp" || event.key === "ArrowRight" ? 1 : -1)
    }
    else if (event.key === "Escape") {
      event.preventDefault()
      onInteraction?.()
      open(home ? "home" : panel === "quick" ? "home" : "quick")
    }
    else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      press()
    }
    else if (event.key.toLowerCase() === "t" && mode === "heat_cool" && home && awake) {
      event.preventDefault()
      onSelectedTargetChange?.(target === "heating" ? "cooling" : "heating")
      onInteraction?.()
    }
  }

  function angleAt(event: PointerEvent) {
    const box = svg.current?.getBoundingClientRect()
    if (!box) {
      return 0
    }
    const dx = event.clientX - box.left - box.width / 2
    const dy = event.clientY - box.top - box.height / 2
    return Math.atan2(dx, -dy) * 180 / Math.PI
  }

  function pointerDown(event: PointerEvent<SVGPathElement>) {
    if (disabled || event.button !== 0) {
      return
    }
    event.preventDefault()
    event.currentTarget.focus()
    if (!interact()) {
      return
    }
    event.currentTarget.setPointerCapture(event.pointerId)
    drag.current = {
      pointer: event.pointerId, angle: angleAt(event), rotation: 0, value, selection, moved: false,
    }
    setDragging(true)
  }

  function pointerMove(event: PointerEvent<SVGPathElement>) {
    const { current } = drag
    if (!current || current.pointer !== event.pointerId || disabled) {
      return
    }
    const angle = angleAt(event)
    current.rotation += nestAngleDelta(current.angle, angle)
    current.angle = angle
    current.moved ||= Math.abs(current.rotation) > 3
    onInteraction?.()
    if (!home) {
      const offset = Math.round(current.rotation / 24)
      setSelection(((current.selection + offset) % count + count) % count)
    }
    else if (canAdjust) {
      const next = nestRingSetPoint(current.value, current.rotation, temperatureUnit, bounds, setPointLimits)
      if (Math.abs(next - value) > 0.00001) {
        onClimateSettingChange?.({ ...climateSetting, [field]: next })
      }
    }
  }

  function pointerUp(event: PointerEvent<SVGPathElement>) {
    const { current } = drag
    if (!current || current.pointer !== event.pointerId) {
      return
    }
    drag.current = null
    setDragging(false)
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    if (!current.moved && event.type === "pointerup") {
      press()
    }
  }

  const readout = `Nest Learning Thermostat, third generation, ${appearance}. `
    + `Room ${ambient} degrees ${temperatureUnit}. ${eco ? "Eco" : MODES.find((item) => item.value === mode)?.label}. `
    + `Heat target ${formatTemperature(climateSetting.heating_set_point_celsius, temperatureUnit)}, `
    + `cool target ${formatTemperature(climateSetting.cooling_set_point_celsius, temperatureUnit)}. `
    + `${activity}. Display ${displayState}. ${isFanRunning ? "Fan running." : ""}`
  const ringLabel = !awake
    ? "Wake thermostat ring"
    : !home
      ? `Turn ring: ${panel === "quick" ? quickSelection.label : items[selection]}`
      : canAdjust ? `Adjust ${target} target` : "Thermostat ring, press for menu"
  const buttonLabel = !awake
    ? "Wake Nest display"
    : home
      ? "Press Nest ring for Quick View"
      : panel === "quick" ? `Select ${quickSelection.label}` : `Select ${items[selection]}`
  const clock = /^([01]?\d|2[0-3]):[0-5]\d$/.test(time) ? time : "10:09"
  const [hour = 10, minute = 9] = clock.split(":").map(Number)
  const clockText = `${hour % 12 || 12}:${String(minute).padStart(2, "0")}`

  return (
    <div
      className={`${styles.device} ${className ?? ""}`}
      data-nest-device={appearance}
      data-appearance={appearance}
      data-activity={activity}
      data-display={displayState}
      data-panel={panel}
      data-dragging={dragging}
      onPointerEnter={(event) => {
        if (!disabled && displayState === "asleep" && event.pointerType === "mouse") {
          onInteraction?.()
          onDisplayStateChange?.("farsight")
        }
      }}
    >
      <span
        className={styles.readout}
        id={`${id}-readout`}
      >
        {readout}
      </span>
      <span
        className={styles.readout}
        id={`${id}-instructions`}
      >
        Drag the outer ring clockwise or counterclockwise. Arrow keys turn the ring; Enter presses it. Escape goes back.
        {mode === "heat_cool" && " T switches the preview between heat and cool targets."}
      </span>
      <svg
        ref={svg}
        className={styles.artwork}
        viewBox="0 0 400 400"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <filter
            id={`${id}-shadow`}
            x="-50%"
            y="-30%"
            width="200%"
            height="200%"
            colorInterpolationFilters="sRGB"
          >
            <feDropShadow
              dx="0"
              dy="4"
              stdDeviation="4"
              floodColor="#090a0b"
              floodOpacity=".10"
            />
            <feDropShadow
              dx="3"
              dy="16"
              stdDeviation="12"
              floodColor="#31353f"
              floodOpacity=".14"
            />
          </filter>
          <linearGradient
            id={`${id}-steel`}
            x1="55"
            y1="38"
            x2="318"
            y2="362"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor={clay ? "#f7f8fa" : "#e7e9e9"} />
            <stop
              offset=".14"
              stopColor={clay ? "#f1f3f5" : "#fff"}
            />
            <stop
              offset=".34"
              stopColor={clay ? "#e2e5e9" : "#aaaeb1"}
            />
            <stop
              offset=".48"
              stopColor={clay ? "#e6e9ed" : "#e9ebed"}
            />
            <stop
              offset=".64"
              stopColor={clay ? "#d9dde3" : "#777d81"}
            />
            <stop
              offset=".79"
              stopColor={clay ? "#d3d8df" : "#c2c6c7"}
            />
            <stop
              offset=".91"
              stopColor={clay ? "#c6cdd6" : "#61666a"}
            />
            <stop
              offset="1"
              stopColor={clay ? "#dde2e8" : "#d4d8da"}
            />
          </linearGradient>
          <linearGradient
            id={`${id}-edge`}
            x1="65"
            y1="52"
            x2="280"
            y2="369"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#fff" />
            <stop
              offset=".48"
              stopColor={clay ? "#edf0f3" : "#d9dddf"}
            />
            <stop
              offset="1"
              stopColor={clay ? "#b7bec9" : "#747b80"}
            />
          </linearGradient>
          <linearGradient
            id={`${id}-glass`}
            x1="85"
            y1="43"
            x2="282"
            y2="331"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor={clay ? "#f3f5f7" : "#646768"} />
            <stop
              offset=".36"
              stopColor={clay ? "#eceff3" : "#111213"}
            />
            <stop
              offset=".68"
              stopColor={clay ? "#e6e9ee" : "#020303"}
            />
            <stop
              offset="1"
              stopColor={clay ? "#d9dfe7" : "#202223"}
            />
          </linearGradient>
          <radialGradient
            id={`${id}-heat`}
            cx="50%"
            cy="25%"
            r="78%"
          >
            <stop stopColor={clay ? "#f0e5df" : "#ef5714"} />
            <stop
              offset=".55"
              stopColor={clay ? "#e9dfd9" : "#e84002"}
            />
            <stop
              offset="1"
              stopColor={clay ? "#e4d8d1" : "#d83700"}
            />
          </radialGradient>
          <radialGradient
            id={`${id}-cool`}
            cx="43%"
            cy="20%"
            r="85%"
          >
            <stop stopColor={clay ? "#e6edf2" : "#2c50e6"} />
            <stop
              offset=".55"
              stopColor={clay ? "#dfe8ef" : "#1741e2"}
            />
            <stop
              offset="1"
              stopColor={clay ? "#d7e1eb" : "#1537cc"}
            />
          </radialGradient>
          <radialGradient
            id={`${id}-reflection`}
            cx="23%"
            cy="0%"
            r="75%"
          >
            <stop
              stopColor="#fff"
              stopOpacity={clay ? ".22" : ".13"}
            />
            <stop
              offset=".66"
              stopColor="#fff"
              stopOpacity="0"
            />
          </radialGradient>
          <clipPath id={`${id}-display`}>
            <circle
              cx="200"
              cy="200"
              r="111"
            />
          </clipPath>
        </defs>
        <g
          className={styles.body}
          aria-hidden="true"
        >
          <circle
            cx="200"
            cy="202"
            r="171"
            fill={clay ? "#d7dce3" : "#8f969a"}
            filter={`url(#${id}-shadow)`}
          />
          <circle
            cx="200"
            cy="200"
            r="171"
            fill={`url(#${id}-steel)`}
            stroke={`url(#${id}-edge)`}
            strokeWidth="2"
          />
          <circle
            cx="200"
            cy="198.5"
            r="166.5"
            stroke={clay ? "#fff" : "#f7f8f8"}
            strokeWidth="1.1"
            opacity=".68"
          />
          <circle
            cx="200"
            cy="200"
            r="157"
            fill={`url(#${id}-glass)`}
            stroke={clay ? "#d9dfe6" : "#424647"}
            strokeWidth=".8"
          />
          <path
            d="M52 148A155 155 0 0 1 313 93"
            stroke="#fff"
            strokeWidth=".7"
            opacity={clay ? ".75" : ".35"}
          />
          <path
            d="M92 314Q200 337 308 314"
            stroke={clay ? "#bfc7d1" : "#5c6060"}
            strokeWidth=".7"
            opacity=".55"
          />
          <path
            d="M94 316Q200 337 306 316"
            stroke={clay ? "#fff" : "#000"}
            strokeWidth="1"
            opacity=".25"
          />
          <image
            href={typeof nestLogo === "string" ? nestLogo : nestLogo.src}
            x="176"
            y="37"
            width="48"
            height="48"
            style={{ filter: clay ? "none" : "brightness(0) invert(1)", opacity: clay ? 0.19 : 0.55 }}
          />
          <circle
            cx="200"
            cy="200"
            r="111"
            fill={clay ? "#e9edf1" : "#070808"}
          />
          <g
            className={styles.screenLayer}
            opacity={displayState === "asleep" || !home ? 0 : 1}
          >
            <circle
              className={styles.screenLayer}
              cx="200"
              cy="200"
              r="111"
              fill={`url(#${id}-heat)`}
              opacity={isHeating ? 1 : 0}
            />
            <circle
              className={styles.screenLayer}
              cx="200"
              cy="200"
              r="111"
              fill={`url(#${id}-cool)`}
              opacity={!isHeating && isCooling ? 1 : 0}
            />
          </g>
          <g
            clipPath={`url(#${id}-display)`}
            className={styles.screen}
          >
            <g className={`${styles.faceLayer} ${!awake || !home ? styles.sleeping : ""}`}>
              {!eco && mode !== "off" && (
                <NestLearningDial
                  targetAngle={targetAngle}
                  ambientAngle={ambientAngle}
                  activityTargetAngle={animateActivity && activityTarget !== undefined
                    ? nestTemperatureAngle(activityTarget, setPointLimits)
                    : undefined}
                  oppositeAngle={mode === "heat_cool" ? nestTemperatureAngle(oppositeValue, setPointLimits) : undefined}
                  ambient={ambient}
                  motionEnabled={awake && home && !reducedMotion}
                  paused={displayPaused}
                />
              )}
              <EcobeeDisplayTransition
                value={`${eco}-${mode}-${displayStatus}-${number}`}
                paused={displayPaused}
                reducedMotion={reducedMotion}
              >
                <g
                  textAnchor="middle"
                  className={styles.activityInk}
                >
                  {mode !== "off" && (
                    <text
                      x="200"
                      y="171"
                      className={styles.label}
                    >
                      {eco ? "ECO TEMPERATURES" : displayStatus}
                    </text>
                  )}
                  <text
                    x={mode === "off" ? 200 : 198}
                    y={mode === "off" ? 214 : 238}
                    className={mode === "off" ? styles.offWord : eco ? styles.numberSmall : styles.number}
                  >
                    {eco ? "eco" : mode === "off" ? "OFF" : number}
                  </text>
                </g>
              </EcobeeDisplayTransition>
              <g
                textAnchor="middle"
                className={styles.status}
              >
                {mode === "heat_cool" && !eco && (
                  <text
                    x="200"
                    y="255"
                    fill="var(--nest-muted)"
                  >
                    {target === "heating" ? "HEAT TO" : "COOL TO"}
                    {" "}
                    {number}
                    {" "}
                    ·
                    {" "}
                    {target === "heating" ? "COOL" : "HEAT"}
                    {" "}
                    {formatTemperature(oppositeValue, temperatureUnit)}
                  </text>
                )}
                {(eco || mode === "off") && (
                  <text
                    x="200"
                    y={mode === "off" ? 240 : 256}
                    fill="var(--nest-muted)"
                  >
                    INSIDE
                    {" "}
                    {ambient}
                    °
                  </text>
                )}
              </g>
              {(showLeaf || eco) && (
                <NestIcon
                  icon="eco"
                  x={200}
                  y={282}
                  color={leafColor}
                />
              )}
              {showFanIndicator && isFanRunning && !showLeaf && !eco && (
                <NestIcon
                  icon="fan"
                  x={200}
                  y={282}
                  color={secondary}
                />
              )}
            </g>
            <g
              className={`${styles.faceLayer} ${displayState !== "farsight" ? styles.sleeping : ""}`}
              textAnchor="middle"
            >
              <text
                x="198"
                y={farsightFace === "clock" ? 224 : 241}
                className={farsightFace === "clock" ? styles.numberSmall : styles.numberFar}
              >
                {farsightFace === "clock" ? clockText : farsightFace === "room" || mode === "off" ? ambient : eco ? "eco" : number}
              </text>
            </g>
            <g className={`${styles.faceLayer} ${!awake || home ? styles.sleeping : ""}`}>
              {panel === "quick"
                ? (
                  <g>
                    {QUICK.map((item, i) => {
                      const position = nestPoint(i * 72, 83)
                      return (
                        <g
                          key={item.icon}
                          className={styles.menuIcon}
                          opacity={selection === i ? 1 : 0.36}
                        >
                          {selection === i && (
                            <circle
                              cx={position.x}
                              cy={position.y}
                              r="15"
                              fill={clay ? "#d5dce5" : "#303334"}
                            />
                          )}
                          <NestIcon
                            icon={item.icon}
                            x={position.x}
                            y={position.y}
                            color={ink}
                          />
                        </g>
                      )
                    })}
                    <text
                      textAnchor="middle"
                      x="200"
                      y="198"
                      className={styles.menuLabel}
                    >
                      {quickSelection.label}
                    </text>
                    <text
                      textAnchor="middle"
                      x="200"
                      y="218"
                      className={styles.menuDetail}
                    >
                      {quickSelection.icon === "mode"
                        ? MODES.find((item) => item.value === mode)?.label
                        : quickSelection.icon === "eco" ? eco ? "On" : "Off" : quickSelection.icon === "fan" ? climateSetting.fan_mode_setting === "on" ? "On" : "Auto" : `${ambient}° inside`}
                    </text>
                  </g>
                )
                : !home && (
                  <g textAnchor="middle">
                    <text
                      x="200"
                      y="139"
                      className={styles.label}
                      fill={secondary}
                    >
                      {panel === "settings" ? "DISPLAY" : panel.toUpperCase()}
                    </text>
                    {items.map((label, i) => {
                      const y = 172 + i * 27
                      return (
                        <g
                          key={label}
                          className={styles.listItem}
                          opacity={selection === i ? 1 : 0.38}
                        >
                          {selection === i && (
                            <rect
                              x="132"
                              y={y - 19}
                              width="136"
                              height="27"
                              rx="13.5"
                              fill={clay ? "#d5dce5" : "#303334"}
                            />
                          )}
                          <text
                            x="200"
                            y={y}
                          >
                            {label}
                          </text>
                        </g>
                      )
                    })}
                  </g>
                )}
            </g>
          </g>
          <circle
            cx="200"
            cy="200"
            r="156"
            fill={`url(#${id}-reflection)`}
            pointerEvents="none"
          />
        </g>
        <path
          d={RING_PATH}
          fill="transparent"
          fillRule="evenodd"
          className={styles.ringControl}
          role={awake && home && canAdjust ? "slider" : "button"}
          tabIndex={disabled ? -1 : 0}
          aria-label={ringLabel}
          aria-describedby={`${id}-instructions ${id}-readout`}
          aria-disabled={disabled}
          aria-valuemin={awake && home && canAdjust ? celsiusToDisplay(bounds.minCelsius, temperatureUnit) : undefined}
          aria-valuemax={awake && home && canAdjust ? celsiusToDisplay(bounds.maxCelsius, temperatureUnit) : undefined}
          aria-valuenow={awake && home && canAdjust ? Number(number) : undefined}
          aria-valuetext={awake && home && canAdjust ? `${number} degrees ${temperatureUnit}` : undefined}
          onKeyDown={keyboard}
          onPointerDown={pointerDown}
          onPointerMove={pointerMove}
          onPointerUp={pointerUp}
          onPointerCancel={pointerUp}
          onLostPointerCapture={() => {
            drag.current = null
            setDragging(false)
          }}
        />
      </svg>
      <button
        type="button"
        className={styles.press}
        disabled={disabled}
        aria-label={buttonLabel}
        aria-describedby={`${id}-instructions ${id}-readout`}
        onClick={press}
        onKeyDown={(event) => {
          if (event.key !== "Enter" && event.key !== " ") {
            keyboard(event)
          }
        }}
      />
    </div>
  )
}
